<template>
  <div class="popup-container">
    <Modal
      ref="modal-component"
      class="helpy-modal"
      :showClose="true"
      :title="getTitle"
      :id="modalId"
      :isOpen="isOpened"
      :isPopup="true"
      :dirty="dirty"
      :size="'80%'"
      :showFooter="false"
      @dynform-close="handleConfirmation"
      @modal-closed="popPopups"
      :key="level"
    >
      <template v-if="urls">
        <div class="form-group">
          <label for="popupFormControlSelect">Event type</label>
          <select class="form-control" id="popupFormControlSelect" v-model="selected">
            <option>Please select an event type</option>
            <option v-for="(url, index) in urls" :key="'url_' + index" :value="url.url">
              {{ url.name }}
            </option>
          </select>
        </div>
        <b-alert show v-if="!url">Please select an event type first</b-alert>
      </template>
      <div v-if="isDynform">
        <dyn-form
          ref="dyn-form"
          v-if="[formType.DYNFORM, formType.FORM_VIEW].includes(type)"
          v-bind="formParams"
          :subForm="subForm"
          :is-read-view="isFormViewActive"
          @dirty-change="dirtyChange"
          @dynform-saved="closeModal"
          @dynform-close="handleConfirmation"
        />
        <FormDataVersion
          v-else-if="type === formType.FORM_DATA_VERSION"
          :url="url"
          @dynform-saved="closeModal"
          @dynform-close="closeModal"
        />
        <helpy-form
          v-else-if="type === formType.HELPY_FORM"
          :url="url"
          @dirty-change="dirtyChange"
          @form-saved="closeModal"
          @form-close="closeModal"
        />
      </div>
      <div v-if="url && subForm">
        <dyn-form
          ref="dyn-form"
          v-if="loaded && [formType.DYNFORM, formType.FORM_VIEW].includes(type)"
          v-bind="formParams"
          :sub-form="subForm"
          :sub-form-temp-data="subFormTempData"
          @dirty-change="dirtyChange"
          :is-read-view="isFormViewActive"
          @dynform-close="handleConfirmation"
        />
      </div>
    </Modal>
    <v-style v-if="customCss"> {{ customCss }}</v-style>
  </div>
</template>

<script>
import Modal from './modal/Modal'
import core from '../utils/mixin-core'
import EventBus from '../utils/event-bus'
import HelpyForm from './helpy-designer/HelpyForm'
import confirmation from '../utils/mixin-confirmation'
import FormDataVersion from './form-version/FormDataVersion'

import parseUrl from '../lib/url/ParseUrl'
import { mapActions, mapState } from 'vuex'
import { set, merge, get, trimStart, cloneDeep, isEmpty, isString } from 'lodash'

import qs from 'qs'
import Vue from 'vue'
import Url from 'url'

import { formType } from '../form'
import { clearEvalCache } from '../store/modules/form/helpers/init-queries'
import { TYPE_DANGER } from '../utils/mixin-notify'

const VIEW_RE = /\/view/

Vue.component('v-style', {
  render: function (createElement) {
    return createElement('style', this.$slots.default)
  },
})

/**
 * @class Popup
 *
 * @description  popup component for showing form and stuff
 */
export default {
  name: 'Popup',
  mixins: [core, confirmation],
  props: {
    level: Number,
  },
  components: {
    Modal,
    FormDataVersion,
    HelpyForm,
  },
  computed: {
    ...mapState('popup', ['popups', 'routedUrl']),
    isDynform() {
      return this.loaded && this.url && !this.subForm
    },
    modalId() {
      return 'popup_modal-' + this.level
    },
    isOpened() {
      return get(this, `popups[${this.level}].isOpened`)
    },
    url() {
      return get(this, `popups[${this.level}].url`, '')
    },
    urls() {
      return get(this, `popups[${this.level}].urls`)
    },
    type() {
      return get(this, `popups[${this.level}].type`)
    },
    subForm() {
      return get(this, `popups[${this.level}].subForm`)
    },
    subFormTempData() {
      return get(this, `popups[${this.level}].subFormTempData`)
    },
    getTitle() {
      const title = get(this, `popups[${this.level}].title`) || this.$i18n.t(get(this, 'item.panel.title', ''))
      return this.loaded ? title : ''
    },
    createNewElement() {
      return get(this, `popups[${this.level}].createNewElement`)
    },
    getRoutedUrl() {
      return get(this, `routedUrl`)
    },
    isFormViewActive() {
      const isView = get(this.formParams, 'popupUrl', '').match(VIEW_RE)
      return this.type === this.formType.FORM_VIEW || !!isView
    },
    getEvent() {
      // @TODO event string kell legyen, a dropdown/sql create-es popupok masik propba tegyek a fieldpath-t
      const event = get(this, `popups[${this.level}].event`)
      return isString(event) ? event : ''
    },
  },
  data: () => {
    return {
      requestControllerCache: '',
      requestRouteCache: '',
      routedUrlCache: '',
      coreParamsCache: '',
      gui: 0,
      item: {},
      loaded: false,
      formParams: {},
      customCss: '',
      selected: 'Please select an event type',
      dirty: false,
      formType,
      VIEW_RE,
    }
  },
  created() {
    EventBus.$on('subFormPopupSave', this.onSubformPopupSave)
    EventBus.$on('close-popup', this.onClosePopup)
  },
  destroyed() {
    EventBus.$off('subFormPopupSave', this.onSubformPopupSave)
    EventBus.$off('close-popup', this.onClosePopup)
  },
  mounted() {
    this.storeRequestRoute()
    this.showModal(this.modalId)
    this.getData()
  },
  beforeDestroy() {
    EventBus.$off('subFormPopupSave', this.onSubformPopupSave)
    EventBus.$off('close-popup', this.onClosePopup)
    this.restoreCoreParams()
  },
  methods: {
    ...mapActions(['showModal', 'hideModal']),
    ...mapActions('popup', [
      'setOpened',
      'setUrl',
      'setUrls',
      'setSubFormTempData',
      'setCreateNewElement',
      'setRoutedUrl',
      'popPopups',
    ]),
    denyPopup() {
      this.closeModal({ formId: this.formParams.formId })
      this.$notify({
        title: this.$i18n.t('access_denied'),
        message: this.$i18n.t('you_do_not_have_permission_for_this_view'),
        type: TYPE_DANGER,
      })
    },
    onClosePopup() {
      this.closeModal({ formId: this.formParams.formId })
    },
    onSubformPopupSave(formId) {
      this.closeModal({ formId })
    },
    storeRequestRoute() {
      if (this.CORE.requestController) {
        this.requestControllerCache = this.CORE.requestController
      }
      if (this.CORE.requestRoute) {
        this.requestRouteCache = this.CORE.requestRoute
      }
      if (this.CORE.routedUrl) {
        this.routedUrlCache = this.CORE.routedUrl
      }
    },
    async getLayout(url) {
      const response = await this.$client.execute({
        operationId: 'LayoutManagers_getPopup',
        parameters: { url },
      })

      if (get(response, 'status', 403) === 403) {
        this.denyPopup()
      }

      return response.body
    },
    async getData() {
      if (!this.url) {
        return
      }

      this.loaded = false
      const layout = await this.getLayout(this.url)
      this.coreParamsCache = cloneDeep(this.$CORE.params)

      if (!layout.form && layout.closeModal) {
        this.loaded = true
        // @todo: remove when use normal API for non form request
        this.closeModal({ submitType: 'save' })
        return
      }

      if (layout.customCss) {
        this.customCss = layout.customCss
      }

      const form = layout.form
      const targetUrl = layout.url
      this.formParams = [this.formType.DYNFORM, this.formType.FORM_VIEW].includes(this.type)
        ? this.paramsTranslate(form.params.base, form.params.dictionary, targetUrl, form.position.i)
        : {}

      const requestController = trimStart(Url.parse(targetUrl).pathname, '/').split('/')[0]

      this.CORE.requestController = requestController
      this.$CORE.requestController = requestController

      const requestRoute = trimStart(Url.parse(layout.url).pathname, '/')
      const requestAction = requestRoute.split('/')[1]

      this.CORE.requestRoute = requestRoute
      this.$CORE.requestRoute = requestRoute
      this.CORE.requestAction = requestAction
      this.$CORE.requestAction = requestAction

      const routedUrl = `${window.location.origin}${this.url}`

      this.CORE.routedUrl = routedUrl
      this.$CORE.routedUrl = routedUrl

      const urlParsed = Url.parse(this.url)
      const mainFormParams = qs.parse(urlParsed.query)

      const itemUrlParsed = Url.parse(get(layout, 'url'))
      const itemUrlparams = qs.parse(itemUrlParsed.query)

      this.formParams.externalParams = Object.assign(mainFormParams, itemUrlparams)

      this.formParams.inPopup = true
      this.formParams.level = this.level
      if (this.subForm) {
        this.formParams.buttons = {
          add: {
            event: 'save',
            title: 'dynform_button_save_record',
            redirectUrl: '',
            hidden: false,
          },
          discard: {
            event: 'index',
            title: 'dynform_button_discard',
            redirectUrl: '',
            hidden: false,
          },
        }
      }

      if (isEmpty(this.getRoutedUrl) && !this.subForm) {
        let popupParams = {
          ...this.$CORE.params,
          ...this.formParams.externalParams,
        }
        popupParams = new URLSearchParams(popupParams).toString()
        const baseUrl = `${window.location.origin}${itemUrlParsed.pathname}`
        this.formParams.popupUrl = `${baseUrl}?${popupParams || ''}`
        this.setRoutedUrl(this.formParams.popupUrl)
      } else {
        this.formParams.popupUrl = this.getRoutedUrl
      }

      Object.assign(this.$CORE.params, mainFormParams)

      const sqlParams = await this.$client.execute({
        operationId: 'ExternalParams_getSqlParams',
        parameters: {
          dotoParams: JSON.stringify(this.$CORE.params),
        },
      })

      _.merge(this.$CORE.sqlParams, get(sqlParams, 'body', {}))

      this.item = form
      this.loaded = true
    },
    setEvent(url, type) {
      this.setUrl({ url, type })
      this.setOpened()
      this.getData()
    },
    paramsTranslate(params, dictionary = {}, urlIn, i) {
      const panelId = `${urlIn}:${i}`
      let translated = {}

      const fakeCOREParams = parseUrl(urlIn)

      if (Array.isArray(dictionary)) {
        dictionary.forEach((el) => {
          if (fakeCOREParams[el.name]) {
            set(translated, el.value, fakeCOREParams[el.name])
          }
        })
      } else {
        translated = Object.keys(dictionary).reduce((acc, curr) => {
          if (curr.includes('#') || curr.includes('DOTO')) {
            const key = curr.replace(/#/g, '.').replace('DOTO.', '')
            const param = get(fakeCOREParams, key, null)
            set(acc, dictionary[curr], param)
          } else if (curr.includes('#') || curr.includes('CORE')) {
            const key = curr.replace(/#/g, '.').replace('CORE.', '')
            const param = get(fakeCOREParams, key, null)
            set(acc, dictionary[curr], param)
          } else if (fakeCOREParams[curr]) {
            set(acc, dictionary[curr], fakeCOREParams[curr])
          }
          return acc
        }, {})
      }

      return merge({ panelId }, translated, params)
    },
    async handleConfirmation(event) {
      let confirmed = false
      const eventType = get(event, 'event', 'index')
      if (this.dirty && eventType === 'index') {
        confirmed = await this._confirm('you_have_unfinished_changes')
      } else {
        confirmed = true
      }
      if (confirmed) this.closeModal(event)
    },
    /**
     * @description Modal bezárását és paraméterek visszaállítását kezeli.
     * @param event
     */
    closeModal(event = {}) {
      clearEvalCache() // clear query param result cache on popup close

      const SAVE_AND_NEW = 'saveAndNew'
      const SAVE = 'save'
      const SAVE_AND_CLOSE = 'saveAndClose'

      if (event.idForm) {
        if (event.idForm !== this.formParams.idForm) return
        EventBus.$emit(`reset-form-list-${event.idForm}`, { idForm: event.idForm })
      }

      if (event.submitType === SAVE_AND_NEW) {
        this.setEvent(event.formUrl, event.submitType)
      }
      if ([SAVE, SAVE_AND_CLOSE].includes(event.submitType)) {
        this.restoreCoreParams()
        const btn = get(this.$refs, `['dyn-form'].getButtons.${event.submitType}`, null)
        if (btn && btn.redirectUrl) {
          location.href = `/${btn.redirectUrl}`
        } else {
          this.level === 0 && !this.subForm && !this.createNewElement && EventBus.$emit(this.getEvent, {})
        }
      }
      if (event.submitType !== SAVE_AND_NEW) {
        EventBus.$emit('popupClose', event)
        this.setSubFormTempData(null)
        this.restoreCoreParams()
        this.hideModal(this.modalId)
      }
      if (this.level === 0) this.setRoutedUrl(null)
    },
    restoreCoreParams() {
      if (this.requestControllerCache) {
        this.CORE.requestController = this.requestControllerCache
        this.$CORE.requestController = this.requestControllerCache
      }
      if (this.requestRouteCache) {
        this.CORE.requestRoute = this.requestRouteCache
        this.$CORE.requestRoute = this.requestRouteCache
      }
      if (this.coreParamsCache) {
        this.CORE.params = this.coreParamsCache
        this.$CORE.params = this.coreParamsCache
      }
      if (this.routedUrlCache) {
        this.CORE.routedUrl = this.routedUrlCache
        this.$CORE.routedUrl = this.routedUrlCache
      }
    },
    dirtyChange(value) {
      this.dirty = value
    },
  },
}
</script>

<style lang="scss" scoped>
.dynform {
  display: block !important;
}
.modal-backdrop {
  z-index: 1000 !important;
}
</style>
